import React, { useEffect, useRef, useState } from 'react';
import { Search, X, User } from 'lucide-react';
import StatusBadge from './StatusBadge';
import { clientesAPI, describirError } from '../services/api';
import '../styles/ClienteBuscador.css';

/**
 * Buscador de clientes reales de Clientes V2. Busca por folio, nombre, RFC,
 * CURP o teléfono vía `clientesAPI.listar({ q })`, el mismo endpoint que
 * usa la lista de Clientes.
 */
function ClienteBuscador({ clienteSeleccionado, onSeleccionar }) {
  const [termino, setTermino] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const cajaRef = useRef(null);

  useEffect(() => {
    if (!termino.trim() || termino.trim().length < 2) {
      setResultados([]);
      return;
    }
    const t = setTimeout(async () => {
      setBuscando(true);
      setError(null);
      try {
        // El backend solo acepta limit 25/50/100 (validation/cliente.js);
        // se pide el mínimo permitido y se recorta a 8 para el desplegable.
        const { data } = await clientesAPI.listar({ q: termino.trim(), limit: 25 });
        setResultados((data.data || []).slice(0, 8));
      } catch (e) {
        setError(describirError(e).mensaje);
        setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [termino]);

  useEffect(() => {
    const cerrarSiAfuera = (e) => {
      if (cajaRef.current && !cajaRef.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', cerrarSiAfuera);
    return () => document.removeEventListener('mousedown', cerrarSiAfuera);
  }, []);

  if (clienteSeleccionado) {
    return (
      <div className="cliente-buscador-seleccionado">
        <span className="avatar">
          <User size={16} strokeWidth={1.75} />
        </span>
        <div className="cliente-buscador-datos">
          <p className="cell-primary">{clienteSeleccionado.nombreCompleto}</p>
          <dl className="cliente-buscador-meta">
            <div><dt>Folio</dt><dd>{clienteSeleccionado.folio}</dd></div>
            <div><dt>RFC</dt><dd>{clienteSeleccionado.rfc || '—'}</dd></div>
            <div><dt>Teléfono</dt><dd>{clienteSeleccionado.telefonoPrincipal || '—'}</dd></div>
          </dl>
        </div>
        <StatusBadge value={clienteSeleccionado.estado} />
        <button
          type="button"
          className="btn btn-ghost btn-icon"
          onClick={() => onSeleccionar(null)}
          aria-label="Quitar cliente seleccionado"
          title="Cambiar cliente"
        >
          <X size={16} strokeWidth={1.75} />
        </button>
      </div>
    );
  }

  return (
    <div className="cliente-buscador" ref={cajaRef}>
      <div className="datatable-search cliente-buscador-input">
        <Search size={16} strokeWidth={1.75} className="datatable-search-icon" />
        <input
          type="search"
          placeholder="Folio, nombre, RFC, CURP o teléfono"
          aria-label="Buscar cliente"
          value={termino}
          onChange={(e) => {
            setTermino(e.target.value);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
        />
      </div>

      {abierto && termino.trim().length >= 2 && (
        <div className="cliente-buscador-resultados">
          {buscando && <p className="cliente-buscador-mensaje">Buscando…</p>}
          {!buscando && error && <p className="cliente-buscador-mensaje is-error">{error}</p>}
          {!buscando && !error && resultados.length === 0 && (
            <p className="cliente-buscador-mensaje">Ningún cliente coincide.</p>
          )}
          {!buscando &&
            resultados.map((c) => (
              <button
                type="button"
                key={c.id}
                className="cliente-buscador-item"
                onClick={() => {
                  onSeleccionar(c);
                  setAbierto(false);
                  setTermino('');
                }}
              >
                <span className="cell-identity-text">
                  <span className="cell-primary">{c.nombreCompleto}</span>
                  <span className="cell-secondary">
                    {c.folio} · {c.telefonoPrincipal || 'sin teléfono'}
                  </span>
                </span>
                <StatusBadge value={c.estado} />
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default ClienteBuscador;
